/**
 * URL ⇄ ShopFilterState round-trip (TNK-124 phase F).
 *
 * The `/shop/inventory` route uses TanStack Router's `validateSearch` to
 * make filter selection survive refresh + browser back/forward + shared
 * links. This module is the single source of truth for the URL schema
 * and the mapping back and forth — kept pure so it's trivial to test
 * and to reason about default omission.
 *
 * Schema:
 *   ?q=…            — search query
 *   ?sort=…         — sort key (default `newest` omitted)
 *   ?game=…         — comma-separated multi-axis
 *   ?rarity=…       — same shape
 *   ?type=…
 *   ?element=…
 *   ?color=…
 *   ?condition=…
 *   ?priceMin=…     — numeric
 *   ?priceMax=…     — numeric (omitted when at/above priceCap)
 *   ?foil=1         — present when foilOnly toggled
 *
 * Defaults are omitted from the URL so the shareable link doesn't carry
 * noise — `/shop/inventory` with no params is the canonical "everything"
 * view.
 */

import {
  defaultFilterState,
  type ShopFilterState,
  type ShopRarity,
  type SortBy,
} from "./shopFilters";

const VALID_SORTS: ReadonlyArray<SortBy> = [
  "newest",
  "price-asc",
  "price-desc",
  "name-asc",
  "rarity",
];

/** URL-shape mirror of ShopFilterState — every field optional so we can
 *  strip defaults out of shareable links. */
export interface ShopUrlSearch {
  q?: string;
  sort?: SortBy;
  game?: string;
  rarity?: string;
  type?: string;
  element?: string;
  color?: string;
  condition?: string;
  priceMin?: number;
  priceMax?: number;
  foil?: number;
}

function readString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  if (typeof v === "string" && v.length > 0) {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

function readSort(v: unknown): SortBy | undefined {
  if (typeof v !== "string") return undefined;
  return (VALID_SORTS as ReadonlyArray<string>).includes(v)
    ? (v as SortBy)
    : undefined;
}

/** Coerce the raw `Record<string, unknown>` TanStack Router hands to
 *  `validateSearch` into a typed `ShopUrlSearch`. Unknown / malformed
 *  values are dropped so a hand-edited URL can't break the page. */
export function parseShopUrlSearch(
  raw: Record<string, unknown>,
): ShopUrlSearch {
  const foilRaw = raw.foil;
  const foil =
    foilRaw === 1 || foilRaw === "1" || foilRaw === true ? 1 : undefined;
  return {
    q: readString(raw.q),
    sort: readSort(raw.sort),
    game: readString(raw.game),
    rarity: readString(raw.rarity),
    type: readString(raw.type),
    element: readString(raw.element),
    color: readString(raw.color),
    condition: readString(raw.condition),
    priceMin: readNumber(raw.priceMin),
    priceMax: readNumber(raw.priceMax),
    foil,
  };
}

/** Inflate the URL shape back into a `ShopFilterState`. `priceCap` is
 *  the slider ceiling — used as the default for `priceMax` when the
 *  URL doesn't carry one. */
export function searchToFilters(
  search: ShopUrlSearch,
  priceCap: number,
): ShopFilterState {
  const base = defaultFilterState(priceCap);
  const csv = (s: string | undefined): string[] =>
    s ? s.split(",").filter((x) => x.length > 0) : [];
  return {
    ...base,
    search: search.q ?? "",
    sortBy: search.sort ?? base.sortBy,
    game: csv(search.game),
    rarity: csv(search.rarity) as ShopRarity[],
    type: csv(search.type),
    element: csv(search.element),
    color: csv(search.color),
    condition: csv(search.condition),
    priceMin: search.priceMin ?? base.priceMin,
    priceMax: search.priceMax ?? priceCap,
    foilOnly: search.foil === 1,
  };
}

/** Compress the filter state into the URL shape. Defaults are stripped
 *  so a fresh page yields the bare `/shop/inventory` URL. */
export function filtersToSearch(
  filters: ShopFilterState,
  priceCap: number,
): ShopUrlSearch {
  const out: ShopUrlSearch = {};
  if (filters.search.trim()) out.q = filters.search.trim();
  if (filters.sortBy !== "newest") out.sort = filters.sortBy;
  if (filters.game.length) out.game = filters.game.join(",");
  if (filters.rarity.length) out.rarity = filters.rarity.join(",");
  if (filters.type.length) out.type = filters.type.join(",");
  if (filters.element.length) out.element = filters.element.join(",");
  if (filters.color.length) out.color = filters.color.join(",");
  if (filters.condition.length) out.condition = filters.condition.join(",");
  if (filters.priceMin > 0) out.priceMin = filters.priceMin;
  if (filters.priceMax > 0 && filters.priceMax < priceCap) {
    out.priceMax = filters.priceMax;
  }
  if (filters.foilOnly) out.foil = 1;
  return out;
}
